import {AjaxDataTypes} from '../../../../shared/AjaxDataTypes.ts'
import {type TranslationObject} from '@colonist/i18n'
import {LobbyImage} from '@colonist/types'
import {type UserState} from '../../../../shared/UserState.ts'
import {notificationController} from '../EntryShared.ts'
import {UIAjaxWrapper} from '../network/UIAjaxWrapper.ts'
import {UIAPIEndpoints} from '../network/UIAPIEndpoints.ts'
import {ViewPopup} from '../ui_popup/ViewPopup.ts'
import {type ViewPopupOptions} from '../ui_popup/ViewPopupOptions.ts'
import {ClientUserState} from '../ClientUserState.ts'

export class UIUtilsFriends {
    static callbackActionOnFriendRequestSent: (() => void)[] = []
    static callbackNotificationFriendRequestResponded: ((username: string, isAccepted: boolean) => Promise<void>)[] = []

    static async addFriendAction(usernameToAdd: string, onSuccessAction?: (becameFriends: boolean) => void) {
        const friendChangeData: AjaxDataTypes.FriendChangeData = {username: usernameToAdd}
        const response = await UIAjaxWrapper.postRequestHandler(UIAPIEndpoints.apiUserProfileAddFriend(), undefined, friendChangeData, 'application/json')
        if(response == undefined) return

        void ClientUserState.refresh()

        const friendData = response.json as AjaxDataTypes.UserFriendsData
        const becameFriends = friendData.friends.some((friend) => friend.username == usernameToAdd)
        const notificationKey: TranslationObject = becameFriends ? {key: 'strings:popups.notification.youAreNowFriendsWith', options: {username: usernameToAdd}} : {key: 'strings:profilePage.friends.friendRequestSent'}
        notificationController.showNotification(LobbyImage.Friends, notificationKey)

        if(onSuccessAction) onSuccessAction(becameFriends)
        for(const callback of UIUtilsFriends.callbackActionOnFriendRequestSent) {
            callback()
        }
    }

    static async removeFriendAction(usernameToRemove: string, onSuccessAction?: () => void) {
        const friendChangeData: AjaxDataTypes.FriendChangeData = {username: usernameToRemove}
        const postRequestResult = await UIAjaxWrapper.postRequestHandler(UIAPIEndpoints.apiUserProfileRemoveFriend(), undefined, friendChangeData, 'application/json')
        if(postRequestResult == undefined) return

        void ClientUserState.refresh()

        notificationController.showNotification(LobbyImage.Friends, {key: 'strings:popups.friendRequests.removed', options: {username: usernameToRemove}})

        if(typeof onSuccessAction == 'function') {
            onSuccessAction()
        }
    }

    static showRemoveFriendPopup(usernameToRemove: string, removeFriendPopupAction: () => void) {
        const removeFriendPopupTitle: TranslationObject = {key: 'strings:profilePage.friends.removeFriend.title'}
        const removeFriendPopupBody: TranslationObject = {key: 'strings:profilePage.friends.removeFriend.body', options: {
            username: usernameToRemove,
        }}
        const removeFriendPopupOptions: ViewPopupOptions = {checkButtonCustomContent: {key: 'strings:profilePage.friends.removeFriend.checkButton'}, xButtonCustomContent: {key: 'strings:profilePage.friends.removeFriend.xButton'}}
        const removeFriendPopup = new ViewPopup(removeFriendPopupTitle, removeFriendPopupBody, removeFriendPopupAction, () => undefined, true, removeFriendPopupOptions)
        removeFriendPopup.show()
    }

    static async getFriends(currentUserState: UserState | undefined): Promise<AjaxDataTypes.UserFriendsData> {
        if(!currentUserState?.isLoggedIn) return new AjaxDataTypes.UserFriendsData()
        const response = await UIAjaxWrapper.getRequestHandler(UIAPIEndpoints.apiUserProfile.friends(), undefined)
        if(response == undefined) return new AjaxDataTypes.UserFriendsData()
        return response.json as AjaxDataTypes.UserFriendsData
    }

    static async getFriendRequestsReceived(currentUserState: UserState | undefined): Promise<AjaxDataTypes.FriendRequestReceivedData[]> {
        if(!currentUserState?.isLoggedIn) return []
        const friendRequestsResponse = await UIAjaxWrapper.getRequestHandler(UIAPIEndpoints.apiFriendRequestsReceived(), undefined)
        if(friendRequestsResponse == undefined) return []
        return friendRequestsResponse.json as AjaxDataTypes.FriendRequestReceivedData[]
    }

    static async friendRequestResponded(username: string, isAccepted: boolean) {
        const friendChangeData: AjaxDataTypes.FriendChangeData = {username: username}
        const api = isAccepted ? UIAPIEndpoints.apiUserProfileAcceptFriend() : UIAPIEndpoints.apiUserProfileRemoveFriend()
        const response = await UIAjaxWrapper.postRequestHandler(api, undefined, friendChangeData, 'application/json')
        if(response == undefined) return

        void ClientUserState.refresh()

        for(const callback of UIUtilsFriends.callbackNotificationFriendRequestResponded) {
            await callback(username, isAccepted)
        }
    }

    static canUseOnlineFriendService(): boolean {
        if(!ClientUserState.userState?.isLoggedIn) return false
        return true
    }

    static setCallbackFriendRequestResponded(callback: (username: string, isAccepted: boolean) => Promise<void>) {
        UIUtilsFriends.callbackNotificationFriendRequestResponded.push(callback)
    }
}
